import { DialogManager } from "../ui/dialog/dialog_manager.js";
import { UpdateRequiredDialog } from "../ui/dialog/update_required_dialog.js";
import { OverlayManager } from "../ui/overlay/overlay_manager.js";
import { LocalizationManager } from "../localization/localization_manager.js";
import { SupportedLanguages } from "../localization/supported_languages.js";
import { ScreenManager, Screens } from "../screen_manager/screen_manager.js";

export class BackButtonHandler
{
  private static instance: BackButtonHandler;
  private webapp: any;
  private is_initialized: boolean = false;
  private android_back_handler_bound: () => void;
  private capacitor_back_listener: any = null;

  private constructor()
  {
    this.webapp = ( window as any )?.Telegram?.WebApp;
    this.android_back_handler_bound = this.handleAndroidBackButton.bind(this);
  }

  public static getInstance(): BackButtonHandler
  {
    if ( !this.instance )
    {
      this.instance = new BackButtonHandler();
    }
    return this.instance;
  }

  public init(): void
  {
    if ( this.is_initialized )
    {
      return;
    }

    this.is_initialized = true;

    if ( this.webapp && typeof this.webapp.onEvent === "function" )
    {
      this.webapp.onEvent( "backButtonClicked", () =>
      {
        this.handleBackButtonClick();
      } );
    }

    ScreenManager.addOnChangeListener( () => this.updateBackButtonVisibility() );
    this.updateBackButtonVisibility();
    this.initAndroidBackHandler();
  }

  private async initAndroidBackHandler(): Promise<void>
  {
    const capacitor = (window as any)?.Capacitor;
    
    if ( capacitor?.Plugins?.App?.addListener )
    {
      try 
      {
        this.capacitor_back_listener = await capacitor.Plugins.App.addListener('backButton', (data: any) => {
          this.handleCapacitorBackButton(data);
        });
        
        this.setupPopstateHandler();
        return;
      }
      catch ( error )
      {
        console.warn( "BackButtonHandler: Failed to set up Capacitor App listener:", error );
      }
    }

    this.setupPopstateHandler();
  }

  private setupPopstateHandler(): void
  {
    const platform = this.getCurrentPlatform();
    if (platform === 'web' || platform === 'telegram') {
      window.addEventListener('popstate', this.android_back_handler_bound);
      this.overrideHistoryMethods();
      this.ensureHistoryState();
    }
    
    window.addEventListener('android-back-button', () => {
      this.handleBackButtonClick();
    });
  }

  private overrideHistoryMethods(): void
  {
    window.history.back = () => {
      this.handleBackButtonClick();
    };
  }

  private ensureHistoryState(): void
  {
    if (window.history.state === null) {
      window.history.replaceState({ backHandler: true, initial: true }, '', window.location.href);
    }
    
    window.history.pushState({ backHandler: true, level: 1 }, '', window.location.href);
  }

  private getCurrentPlatform(): string
  {
    const capacitor = (window as any)?.Capacitor;
    if ( capacitor?.platform ) 
    {
      return capacitor.platform;
    }
    
    if ( this.webapp )
    {
      return "telegram";
    }
    
    return "web";
  }

  private pushBackState(): void
  {
    window.history.pushState({ backHandler: true }, '', window.location.href);
  }

  private handleCapacitorBackButton(data: any): void
  {
    this.handleBackButtonClick();
  }

  private handleAndroidBackButton(): void
  {
    const platform = this.getCurrentPlatform();
    if (platform === 'web' || platform === 'telegram') {
      this.ensureHistoryState();
    }
    
    this.handleBackButtonClick();
  }

  private async handleBackButtonClick(): Promise<void>
  {
    if ( OverlayManager.isAnyOverlayOpened() )
    {
      OverlayManager.closeOverlay();
      // void DevToDev.backButton('close_dialog');
      return;
    }

    if ( DialogManager.isAnyDialogOpened() )
    {
      const top_dialog = DialogManager.getTopDialog();
      if ( top_dialog instanceof UpdateRequiredDialog && top_dialog.isHardBlocking() )
        return;

      DialogManager.closeDialog();
      // void DevToDev.backButton('close_dialog');
      return;
    }

    const current_screen = ScreenManager.getCurrentScreen();
    if ( current_screen === Screens.GAME )
    {
      const game_screen = ScreenManager.getCurrentScreenInstance() as { onExit?: () => void } | null;
      game_screen?.onExit?.();
      return;
    }

    if ( current_screen !== null && current_screen !== Screens.MENU )
    {
      await ScreenManager.showScreen( Screens.MENU );
      return;
    }

    // if ( current_screen === Screens.GAME )
    // {
    //   this.showExitFromBattleDialog();
    //   void DevToDev.backButton('exit');
    //   return;
    // }

    // if ( current_screen === Screens.MENU )
    // {
    //   await this.showExitConfirmation();
    //   void DevToDev.backButton('exit');
    //   return;
    // }
    
    // const success: boolean = ScreenManager.goBack();

    // if ( !success )
    // {
    //   await this.showExitConfirmation();
    //   void DevToDev.backButton('exit');
    // }
    // else
    // {
    //   void DevToDev.backButton('goBack');
    // }
  }

  private getTelegramUserLanguage(): SupportedLanguages
  {
    try 
    {
      if ( this.webapp?.initDataUnsafe?.user?.language_code )
      {
        const telegram_language_code: string = this.webapp.initDataUnsafe.user.language_code;
        
        const supported_languages_values: string[] = Object.values( SupportedLanguages ) as string[];
        if ( supported_languages_values.includes( telegram_language_code ) )
        {
          return telegram_language_code as SupportedLanguages;
        }
      }
    }
    catch ( error )
    {
      console.warn( 'BackButtonHandler: Failed to get Telegram user language:', error );
    }
    
    return SupportedLanguages.DEFAULT_LANGUAGE;
  }

  private async showExitConfirmation(): Promise<void>
  {
    const current_language: SupportedLanguages = LocalizationManager.getCurrentLanguage();
    const localization = LocalizationManager.getInstance();
    
    const title: string = localization.getText( current_language, "CLOSE_GAME_DIALOG.TITLE" );
    const message: string = localization.getText( current_language, "CLOSE_GAME_DIALOG.TEXT" );
    const exitButton: string = localization.getText( current_language, "CLOSE_GAME_DIALOG.EXIT_BUTTON" );
    const cancelButton: string = localization.getText( current_language, "CLOSE_GAME_DIALOG.CANCEL_BUTTON" );

    const capacitor = (window as any)?.Capacitor;
    if ( capacitor?.Plugins?.Dialog?.confirm )
    {
      try 
      {
        // void DevToDev.exitConfirmShown();
        const result = await capacitor.Plugins.Dialog.confirm({
          title: title,
          message: message,
          okButtonTitle: exitButton,
          cancelButtonTitle: cancelButton
        });
        
        if ( result.value )
        {
          // void DevToDev.exitConfirmResult(true);
          await this.closeAndroidApp();
        }
        // else { void DevToDev.exitConfirmResult(false); }
        return;
      }
      catch ( error )
      {
        console.warn( "BackButtonHandler: Capacitor Dialog failed:", error );
      }
    }

    if ( this.webapp && typeof this.webapp.showConfirm === "function" )
    {
      // void DevToDev.exitConfirmShown();
      this.webapp.showConfirm(
        message,
        ( confirmed: boolean ) =>
        {
          if ( confirmed )
          {
            // void DevToDev.exitConfirmResult(true);
            this.webapp.close();
          }
          // else { void DevToDev.exitConfirmResult(false); }
        }
      );
      return;
    }
    
    // void DevToDev.exitConfirmShown();
    const confirmed = window.confirm( message );
    if ( confirmed )
    {
      // void DevToDev.exitConfirmResult(true);
      await this.closeAndroidApp();
    }
    // else { void DevToDev.exitConfirmResult(false); }
  }

  private async closeAndroidApp(): Promise<void>
  {
    const capacitor = (window as any)?.Capacitor;
    if ( capacitor?.Plugins?.App?.exitApp )
    {
      try 
      {
        await capacitor.Plugins.App.exitApp();
        return;
      }
      catch ( error )
      {
        console.warn( "BackButtonHandler: App.exitApp() failed:", error );
      }
    }

    try
    {
      window.close();
    }
    catch ( error )
    {
      console.warn( "BackButtonHandler: Cannot close app programmatically:", error );
      window.history.back();
    }
  }

  // private showExitFromBattleDialog(): void
  // {
  //   const game_screen = ScreenManager.getInstance( Screens.GAME ) as GameScreen;
  //   const score = game_screen.getScore().toString();
  //   const difficulty = game_screen.getDifficulty();
  //   const finished_adventure = game_screen.getFinishedAdventure();
  //   const dialog: ExitFromBattleDialog = new ExitFromBattleDialog( score, difficulty, finished_adventure );

  //   DialogManager.openDialog( dialog );
  // }

  public updateBackButtonVisibility(): void
  {
    if ( this.is_initialized && this.webapp?.BackButton )
    {
      const has_overlays: boolean = OverlayManager.isAnyOverlayOpened();
      const has_dialogs: boolean = DialogManager.isAnyDialogOpened();
      const current_screen = ScreenManager.getCurrentScreen();
      const has_nested_screen = current_screen !== null && current_screen !== Screens.MENU;

      if ( has_overlays || has_dialogs || has_nested_screen )
      {
        this.webapp.BackButton.show();
      } else
      {
        this.webapp.BackButton.hide();
      }
    }

    if ( this.is_initialized )
    {
      const platform = this.getCurrentPlatform();
      if (platform === 'web' || platform === 'telegram') {
        this.ensureHistoryState();
      }
    }
  }

  public destroy(): void
  {
    if ( !this.is_initialized )
    {
      return;
    }

    if ( this.webapp && typeof this.webapp.offEvent === "function" )
    {
      this.webapp.offEvent( "backButtonClicked" );
    }

    if ( this.webapp?.BackButton )
    {
      this.webapp.BackButton.hide();
    }

    if ( this.capacitor_back_listener )
    {
      try 
      {
        this.capacitor_back_listener.remove();
      }
      catch ( error )
      {
        console.warn( "BackButtonHandler: Failed to remove Capacitor listener:", error );
      }
      this.capacitor_back_listener = null;
    }

    window.removeEventListener('popstate', this.android_back_handler_bound);
    window.removeEventListener('android-back-button', this.handleBackButtonClick);

    this.is_initialized = false;
  }
}
